const API_BASE = '/api/compliance/policies';

const getCsrfToken = () => document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';

function getAuthHeaders(): HeadersInit {
  const token = sessionStorage.getItem('impersonation_token') || localStorage.getItem('token');
  if (!token) throw new Error('No authentication token found');
  return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() };
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(err.error || err.message || `API Error: ${res.status}`);
  }
  return res.json();
}

export interface Policy {
  id: number;
  company_id: number;
  title: string;
  category: string;
  content?: string;
  version: string;
  status: 'draft' | 'active' | 'retired';
  owner_id?: number;
  owner_name?: string;
  approved_by?: number;
  review_due_at?: string;
  acknowledgement_count?: number;
  created_at: string;
  updated_at: string;
}

export interface PolicyStats {
  total: number;
  draft: number;
  active: number;
  retired: number;
  pending_acknowledgement: number;
  due_for_review: number;
}

export const policiesApi = {
  async list(params?: { page?: number; limit?: number; status?: string; category?: string; company_id?: number }): Promise<{ policies: Policy[]; total: number }> {
    const q = new URLSearchParams();
    if (params?.page) q.set('page', String(params.page));
    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.status) q.set('status', params.status);
    if (params?.category) q.set('category', params.category);
    if (params?.company_id) q.set('company_id', String(params.company_id));
    const res = await fetch(`${API_BASE}?${q}`, { headers: getAuthHeaders() });
    return handleResponse(res);
  },

  async create(data: { company_id: number; title: string; category: string; content: string; owner_id?: number; review_due_at?: string }): Promise<Policy> {
    const res = await fetch(API_BASE, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async get(id: number): Promise<Policy> {
    const res = await fetch(`${API_BASE}/${id}`, { headers: getAuthHeaders() });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async update(id: number, data: Partial<Policy>): Promise<Policy> {
    const res = await fetch(`${API_BASE}/${id}`, { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(data) });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async approve(id: number): Promise<Policy> {
    const res = await fetch(`${API_BASE}/${id}/approve`, { method: 'PUT', headers: getAuthHeaders() });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async retire(id: number): Promise<Policy> {
    const res = await fetch(`${API_BASE}/${id}/retire`, { method: 'PUT', headers: getAuthHeaders() });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async acknowledge(id: number): Promise<void> {
    const res = await fetch(`${API_BASE}/${id}/acknowledge`, { method: 'POST', headers: getAuthHeaders() });
    await handleResponse(res);
  },

  async getAcknowledgements(id: number): Promise<any[]> {
    const res = await fetch(`${API_BASE}/${id}/acknowledgements`, { headers: getAuthHeaders() });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async getPendingAcknowledgements(): Promise<Policy[]> {
    const res = await fetch(`${API_BASE}/pending-acknowledgements`, { headers: getAuthHeaders() });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async getDueForReview(companyId?: number): Promise<Policy[]> {
    const q = companyId ? `?company_id=${companyId}` : '';
    const res = await fetch(`${API_BASE}/due-for-review${q}`, { headers: getAuthHeaders() });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async getStats(companyId?: number): Promise<PolicyStats> {
    const q = companyId ? `?company_id=${companyId}` : '';
    const res = await fetch(`${API_BASE}/stats${q}`, { headers: getAuthHeaders() });
    const result: any = await handleResponse(res);
    return result.data || result;
  },
};
